import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Editor from '@monaco-editor/react';
import { alpha, Box, Button, GlobalStyles, Typography, useTheme } from '@mui/material';
import RoundedBox from 'app/shared/componentsRestyled/common/RoundedBox';
import _ from 'lodash';
import PropTypes from 'prop-types';

import LintPanel from '../../../VisualExpressionBuilder/components/LintPanel';
import { getModelPositionFromFlatPosition } from '../../ExpressionEditor.utils';
import useExpressionAutocomplete from '../../hooks/useExpressionAutocomplete';
import AutocompletePopover from '../AutocompletePopover';
import OutlineNavigator from '../OutlineNavigator';

import {
  advancedNoteBadgeStyles,
  advancedNoteStyles,
  charCountStyles,
  editorColumnStyles,
  editorHeaderHintStyles,
  editorHeaderSpacerStyles,
  editorHeaderStyles,
  editorSurfaceStyles,
  editorToolbarStyles,
  editorWrapperStyles,
  getHoverDecorationGlobalStyles,
  getStatusPillStyles,
  getStudioLayoutStyles,
  ghostButtonStyles,
  pinnedReadbackStyles,
  railColumnStyles,
  readbackTagStyles,
  scopeReadoutStyles,
  statusDotStyles,
} from './AdvancedEditorPanel.styles';
import { AdvancedEditorPanelTestIds } from './AdvancedEditorPanel.testIds';

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const HOVER_DECORATION_OPTIONS = {
  inlineClassName: 'expression-outline-hover-inline',
};

const parseHelperTextSpan = helperText => {
  if (!helperText || helperText.indexOf('[') <= 0) {
    return null;
  }

  const raw = helperText.slice(helperText.indexOf('[') + 1, helperText.indexOf(']'));
  const [start, end] = raw.split(',').map(Number);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }

  return { start, end };
};

const collectProblemIds = (nodes, span, acc) => {
  if (!span) {
    return acc;
  }

  nodes.forEach(node => {
    const hasRange = _.isNumber(node.start) && _.isNumber(node.end);
    const overlaps = hasRange && node.start <= span.end && node.end >= span.start;

    if (overlaps) {
      acc.add(node.id);
    }

    if (node.children?.length) {
      collectProblemIds(node.children, span, acc);
    }
  });

  return acc;
};

const AdvancedEditorPanel = ({
  beforeMount,
  editorRef,
  editorValue,
  helperText,
  isFullyVisual,
  isNewExpressionsParsingLogicEnabled,
  isOnlyBooleanAllowed,
  languageId,
  lintFindings,
  monacoRef,
  outlineNodes,
  readbackText = '',
  schemaParams,
  themeId,
  wrapperProps,
  editorOptions,
  onChangeValue,
  onMount,
  onOutlineNodeClick,
}) => {
  const theme = useTheme();
  const decorationIdsRef = useRef([]);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [isEditorMounted, setIsEditorMounted] = useState(false);
  const [showOutline, setShowOutline] = useState(false);

  const hoverDecorationGlobalStyles = useMemo(
    () => getHoverDecorationGlobalStyles(alpha(theme.palette.primary.main, 0.16)),
    [theme],
  );

  const onToggleOutline = useCallback(() => setShowOutline(value => !value), []);
  const hasOutline = outlineNodes.length > 0;

  const isReadOnly = !!editorOptions?.readOnly;

  const {
    state: autocompleteState,
    onHover,
    onSelect,
    trigger: triggerAutocomplete,
  } = useExpressionAutocomplete({
    editorRef,
    monacoRef,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
    isReadOnly,
    isMounted: isEditorMounted,
  });

  const handleMount = useCallback(
    (editor, monaco) => {
      onMount(editor, monaco);
      setIsEditorMounted(true);
    },
    [onMount],
  );

  const errorSpan = useMemo(() => parseHelperTextSpan(helperText), [helperText]);

  const problemIds = useMemo(
    () => collectProblemIds(outlineNodes, errorSpan, new Set()),
    [outlineNodes, errorSpan],
  );

  const showAdvancedNote = isFullyVisual === false;
  const hasReadback = !!readbackText;

  const hasError = !!(helperText && helperText.trim());
  const problemCount = lintFindings.length + (hasError ? 1 : 0);
  const charCount = editorValue.length;
  const lineCount = editorValue ? editorValue.split('\n').length : 0;
  const statusText = problemCount === 0 ? 'Valid' : pluralize(problemCount, 'problem');

  const scopeReadout = useMemo(() => {
    const scope = autocompleteState?.scope;

    if (!scope) {
      return null;
    }

    const caret = autocompleteState?.caret;
    const caretText = caret ? `caret ${caret.line}:${caret.column} · ` : '';

    return `${caretText}scope ${scope.iterator} : ${scope.collection}`;
  }, [autocompleteState]);

  const runEditorAction = useCallback(
    actionId => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      editor.focus();
      editor.getAction(actionId)?.run();
    },
    [editorRef],
  );

  const onReformat = useCallback(
    () => runEditorAction('editor.action.formatDocument'),
    [runEditorAction],
  );

  const onTriggerSuggest = useCallback(() => {
    editorRef.current?.focus();
    triggerAutocomplete();
  }, [editorRef, triggerAutocomplete]);

  const preventEditorBlur = useCallback(event => event.preventDefault(), []);

  const onWrapInGroup = useCallback(() => {
    const editor = editorRef.current;

    if (!editor) {
      return;
    }

    const current = editor.getValue();

    if (!current.trim()) {
      return;
    }

    editor.setValue(`AND(${current})`);
    editor.focus();
    editor.getAction('editor.action.formatDocument')?.run();
  }, [editorRef]);

  const clearDecorations = useCallback(() => {
    if (editorRef.current && decorationIdsRef.current.length) {
      decorationIdsRef.current = editorRef.current.deltaDecorations(decorationIdsRef.current, []);
    }
  }, [editorRef]);

  const onNodeHover = useCallback(
    node => {
      setActiveNodeId(node ? node.id : null);

      if (!editorRef.current || !monacoRef.current || !node || node.start === null) {
        clearDecorations();

        return;
      }

      const range = getModelPositionFromFlatPosition({
        modelValue: editorRef.current.getValue(),
        position: { start: node.start, end: node.end ?? node.start },
      });

      decorationIdsRef.current = editorRef.current.deltaDecorations(decorationIdsRef.current, [
        {
          range: new monacoRef.current.Range(
            range.startLineNumber,
            range.startColumn,
            range.endLineNumber,
            range.endColumn,
          ),
          options: HOVER_DECORATION_OPTIONS,
        },
      ]);
    },
    [editorRef, monacoRef, clearDecorations],
  );

  useEffect(() => clearDecorations, [clearDecorations]);

  return (
    <Box sx={getStudioLayoutStyles(showOutline && hasOutline)}>
      <GlobalStyles styles={hoverDecorationGlobalStyles} />
      <Box sx={editorColumnStyles}>
        <Box sx={editorHeaderStyles}>
          Raw expression
          <Box component="span" sx={editorHeaderHintStyles}>
            · CriteriaExpressionItem · click a token to edit, hover to trace
          </Box>
          <Box sx={editorHeaderSpacerStyles} />
          <Box component="span" sx={getStatusPillStyles(problemCount > 0)}>
            <Box component="span" sx={statusDotStyles} />
            {statusText}
          </Box>
        </Box>

        <Box sx={editorSurfaceStyles}>
          <RoundedBox customStyles={editorWrapperStyles}>
            <Editor
              beforeMount={beforeMount}
              className="code-editor"
              defaultLanguage={languageId}
              height="100%"
              options={editorOptions}
              theme={themeId}
              value={editorValue}
              width="100%"
              wrapperProps={wrapperProps}
              onChange={onChangeValue}
              onMount={handleMount}
            />
          </RoundedBox>
          {autocompleteState && (
            <AutocompletePopover
              activeIndex={autocompleteState.activeIndex}
              anchor={autocompleteState.anchor}
              groups={autocompleteState.groups}
              scope={autocompleteState.scope}
              typed={autocompleteState.typed}
              onHover={onHover}
              onSelect={onSelect}
            />
          )}
        </Box>

        <Box sx={editorToolbarStyles}>
          <Button
            data-testid={AdvancedEditorPanelTestIds.INSERT_BUTTON}
            disabled={isReadOnly}
            size="small"
            sx={ghostButtonStyles}
            variant="outlined"
            onClick={onTriggerSuggest}
            onMouseDown={preventEditorBlur}
          >
            + Insert
          </Button>
          {isOnlyBooleanAllowed && (
            <Button
              data-testid={AdvancedEditorPanelTestIds.WRAP_IN_GROUP_BUTTON}
              disabled={isReadOnly}
              size="small"
              sx={ghostButtonStyles}
              variant="outlined"
              onClick={onWrapInGroup}
            >
              ⤿ Wrap in group
            </Button>
          )}
          <Button
            data-testid={AdvancedEditorPanelTestIds.REFORMAT_BUTTON}
            disabled={isReadOnly}
            size="small"
            sx={ghostButtonStyles}
            variant="outlined"
            onClick={onReformat}
          >
            ⌘↵ Reformat
          </Button>
          {hasOutline && (
            <Button
              data-testid={AdvancedEditorPanelTestIds.TOGGLE_OUTLINE_BUTTON}
              size="small"
              sx={ghostButtonStyles}
              variant="outlined"
              onClick={onToggleOutline}
            >
              {showOutline ? 'Hide outline' : 'Show outline'}
            </Button>
          )}
          {scopeReadout ? (
            <Typography sx={scopeReadoutStyles} variant="regular12">
              {scopeReadout}
            </Typography>
          ) : (
            <Typography sx={charCountStyles} variant="regular12">
              {`${pluralize(charCount, 'char')} · ${pluralize(lineCount, 'line')} · ${pluralize(
                problemCount,
                'problem',
              )}`}
            </Typography>
          )}
        </Box>

        <Box sx={pinnedReadbackStyles}>
          <Typography sx={readbackTagStyles} variant="regular12">
            Reads as
          </Typography>
          <Typography color={hasReadback ? 'text.primary' : 'text.secondary'} variant="regular12">
            {hasReadback ? readbackText : 'Start typing to see a plain-English summary.'}
          </Typography>
        </Box>

        {showAdvancedNote && (
          <Box sx={advancedNoteStyles}>
            <Box sx={advancedNoteBadgeStyles}>
              <Typography variant="regular12">Advanced-only</Typography>
            </Box>
            <Typography color="text.secondary" variant="regular12">
              This rule uses an advanced expression that can&apos;t be shown as blocks.
            </Typography>
          </Box>
        )}

        <LintPanel findings={lintFindings} />
      </Box>

      {showOutline && hasOutline && (
        <Box sx={railColumnStyles}>
          <OutlineNavigator
            activeId={activeNodeId}
            nodes={outlineNodes}
            problemIds={problemIds}
            onNodeClick={onOutlineNodeClick}
            onNodeHover={onNodeHover}
          />
        </Box>
      )}
    </Box>
  );
};

AdvancedEditorPanel.propTypes = {
  beforeMount: PropTypes.func.isRequired,
  editorRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  monacoRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  editorValue: PropTypes.string.isRequired,
  helperText: PropTypes.string.isRequired,
  languageId: PropTypes.string.isRequired,
  lintFindings: PropTypes.array.isRequired,
  outlineNodes: PropTypes.array.isRequired,
  schemaParams: PropTypes.object.isRequired,
  themeId: PropTypes.string.isRequired,
  wrapperProps: PropTypes.object.isRequired,
  editorOptions: PropTypes.object.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  onMount: PropTypes.func.isRequired,
  onOutlineNodeClick: PropTypes.func.isRequired,
  isFullyVisual: PropTypes.bool,
  isNewExpressionsParsingLogicEnabled: PropTypes.bool,
  isOnlyBooleanAllowed: PropTypes.bool,
  readbackText: PropTypes.string,
};

export default memo(AdvancedEditorPanel);
